import { StepperFooter, toast } from '@folio/components';
import { AssetClaimsModule } from '@folio/modules';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  type Metadata,
  claimsControllerListQueryOptions,
  toKeyValueFields,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import { useAssertedParams } from '@folio/lib';
import { useStepperContext } from '@folio/components';
import { useKorintConfig } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';

export function MultiRisquesProClaimsStep() {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();

  const claimsConfig = usePolicyProductConfig('claims');
  const { korintConfig } = useKorintConfig();
  const assetClaimsConfig = korintConfig.modulesConfig?.assetClaims;

  const { data: assets, isLoading: isAssetsLoading } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId, {
      branchId,
    }),
    enabled: !!policyId,
  });

  const assetIds = assets?.data?.map(asset => asset.id) || [];

  const assetClaimsQueries = useQueries({
    queries: assetIds.map(assetId => ({
      ...claimsControllerListQueryOptions({
        externalId: assetId,
        metadata: assetClaimsConfig?.claimsMetadata
          ? { and: toKeyValueFields(assetClaimsConfig.claimsMetadata) as Metadata[] }
          : undefined,
        pageInfo: { count: 10 },
        order: { by: 'createdAt', direction: 'desc' },
      }),
    })),
  });

  const handleNext = async () => {
    const results = await Promise.all(assetClaimsQueries.map(query => query.refetch()));
    const freshClaims = results.flatMap(result => result.data?.data || []);

    for (const claim of freshClaims) {
      const claimCategoryConfig = claimsConfig.claimsCategories.find(
        ({ category }) => category === claim.category,
      );

      if (!claim.responsibility && claimCategoryConfig?.hasResponsibility) {
        toast({
          title: t('claims.incompleteClaimsToast.title'),
          description: t('claims.incompleteClaimsToast.description'),
        });

        return;
      }
    }

    stepper.next();
  };

  return (
    <div className="grid gap-4">
      <AssetClaimsModule />
      <StepperFooter nextButtonEnabled onNext={handleNext} loading={isAssetsLoading} />
    </div>
  );
}
